/**
 * Error Type System
 *
 * Provides a unified error handling system for the frontend application.
 * Converts various error types (ConnectError, TypeError, Error) into
 * standardized AppError subclasses with user-friendly messages.
 */

import { Code, ConnectError } from "@connectrpc/connect";

/**
 * Standard error codes for all application errors
 */
export enum ErrorCode {
  // Network errors
  NETWORK_FAILED = "NETWORK_FAILED",
  NETWORK_TIMEOUT = "NETWORK_TIMEOUT",
  NETWORK_OFFLINE = "NETWORK_OFFLINE",

  // Service errors
  SERVICE_UNAVAILABLE = "SERVICE_UNAVAILABLE",
  SERVICE_MAINTENANCE = "SERVICE_MAINTENANCE",

  // Auth errors
  UNAUTHENTICATED = "UNAUTHENTICATED",
  PERMISSION_DENIED = "PERMISSION_DENIED",
  SESSION_EXPIRED = "SESSION_EXPIRED",

  // Resource errors
  NOT_FOUND = "NOT_FOUND",
  RESOURCE_EXHAUSTED = "RESOURCE_EXHAUSTED",
  ALREADY_EXISTS = "ALREADY_EXISTS",
  INVALID_ARGUMENT = "INVALID_ARGUMENT",

  // Cancellation
  ALREADY_CANCELLED = "ALREADY_CANCELLED",

  // Unknown
  UNKNOWN = "UNKNOWN",
}

/**
 * Base error class for all application errors
 */
export class AppError extends Error {
  code: ErrorCode;
  userMessage: string;
  retryable: boolean;
  originalError?: unknown;

  constructor(code: ErrorCode, userMessage: string, options?: { retryable?: boolean; originalError?: unknown }) {
    super(userMessage);
    this.name = "AppError";
    this.code = code;
    this.userMessage = userMessage;
    this.retryable = options?.retryable ?? false;
    this.originalError = options?.originalError;

    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
}

/**
 * Network-related errors (connection failures, timeouts, offline)
 */
export class NetworkError extends AppError {
  constructor(code: ErrorCode, userMessage: string, options?: { originalError?: unknown }) {
    super(code, userMessage, { retryable: true, ...options });
    this.name = "NetworkError";
  }
}

/**
 * Service-related errors (unavailable, maintenance)
 */
export class ServiceError extends AppError {
  constructor(code: ErrorCode, userMessage: string, options?: { originalError?: unknown }) {
    super(code, userMessage, { retryable: true, ...options });
    this.name = "ServiceError";
  }
}

/**
 * Authentication-related errors (unauthenticated, session expired)
 */
export class AuthError extends AppError {
  constructor(code: ErrorCode, userMessage: string, options?: { originalError?: unknown }) {
    super(code, userMessage, { retryable: false, ...options });
    this.name = "AuthError";
  }
}

/**
 * Convert any error into a standardized AppError
 *
 * @param error - The error to normalize (can be any type)
 * @returns A standardized AppError instance
 */
export function normalizeError(error: unknown): AppError {
  // If already our custom error type, return as-is (idempotent)
  if (error instanceof AppError) {
    return error;
  }

  // Handle ConnectError from @connectrpc/connect
  if (error instanceof ConnectError) {
    switch (error.code) {
      case Code.Unavailable: // Service temporarily unavailable (deployment, restart)
        return new ServiceError(ErrorCode.SERVICE_UNAVAILABLE, "Service temporarily unavailable. Please try again later.", {
          originalError: error,
        });

      case Code.Unknown:
        // Check for network-specific messages
        if (error.message.includes("Failed to fetch") || error.message.includes("NetworkError") || error.message.includes("ECONNREFUSED")) {
          return new NetworkError(ErrorCode.NETWORK_FAILED, "Unable to connect to the server. Please check your internet connection.", {
            originalError: error,
          });
        }
        break;

      case Code.Unauthenticated:
        return new AuthError(ErrorCode.UNAUTHENTICATED, "Please sign in to continue", { originalError: error });

      case Code.PermissionDenied:
        return new AuthError(ErrorCode.PERMISSION_DENIED, "You don't have permission to perform this action", { originalError: error });

      case Code.NotFound:
        return new AppError(ErrorCode.NOT_FOUND, "The requested resource was not found", { retryable: false, originalError: error });

      case Code.ResourceExhausted:
        return new AppError(ErrorCode.RESOURCE_EXHAUSTED, "Resource quota exceeded. Please try again later", {
          retryable: true,
          originalError: error,
        });

      case Code.AlreadyExists:
        return new AppError(ErrorCode.ALREADY_EXISTS, "This resource already exists", { retryable: false, originalError: error });

      case Code.InvalidArgument:
        return new AppError(ErrorCode.INVALID_ARGUMENT, "Invalid input provided. Please check your data", {
          retryable: false,
          originalError: error,
        });

      case Code.Canceled:
        return new AppError(ErrorCode.ALREADY_CANCELLED, "Operation was cancelled", { retryable: false, originalError: error });

      case Code.DeadlineExceeded:
        return new NetworkError(ErrorCode.NETWORK_TIMEOUT, "Request timed out. Please try again", { originalError: error });
    }
  }

  // Handle TypeError (typically network-related in browser)
  if (error instanceof TypeError) {
    const message = error.message.toLowerCase();
    if (message.includes("failed to fetch") || message.includes("network error") || message.includes("connection refused")) {
      return new NetworkError(ErrorCode.NETWORK_FAILED, "Unable to connect to the server. Please check your internet connection.", {
        originalError: error,
      });
    }
  }

  // Handle standard Error
  if (error instanceof Error) {
    return new AppError(ErrorCode.UNKNOWN, error.message || "An unknown error occurred", { retryable: false, originalError: error });
  }

  // Handle string errors
  if (typeof error === "string") {
    const message = error.toLowerCase();
    if (message.includes("failed to fetch") || message.includes("network error") || message.includes("connection refused")) {
      return new NetworkError(ErrorCode.NETWORK_FAILED, "Unable to connect to the server. Please check your internet connection.", {
        originalError: error,
      });
    }
    return new AppError(ErrorCode.UNKNOWN, error, { retryable: false, originalError: error });
  }

  // Fallback for unknown error types
  return new AppError(ErrorCode.UNKNOWN, "An unknown error occurred", { retryable: false, originalError: error });
}

/**
 * Type guard to check if an error is a NetworkError
 */
export function isNetworkError(error: unknown): boolean {
  const normalized = normalizeError(error);
  return (
    normalized instanceof NetworkError ||
    normalized.code === ErrorCode.NETWORK_FAILED ||
    normalized.code === ErrorCode.NETWORK_TIMEOUT ||
    normalized.code === ErrorCode.NETWORK_OFFLINE
  );
}

/**
 * Type guard to check if an error is an AuthError
 */
export function isAuthError(error: unknown): boolean {
  const normalized = normalizeError(error);
  return (
    normalized instanceof AuthError ||
    normalized.code === ErrorCode.UNAUTHENTICATED ||
    normalized.code === ErrorCode.PERMISSION_DENIED ||
    normalized.code === ErrorCode.SESSION_EXPIRED
  );
}

/**
 * Type guard to check if an error is a permission error
 */
export function isPermissionError(error: unknown): boolean {
  const normalized = normalizeError(error);
  return normalized.code === ErrorCode.PERMISSION_DENIED;
}

/**
 * Type guard to check if an error is a "not found" error
 */
export function isNotFoundError(error: unknown): boolean {
  const normalized = normalizeError(error);
  return normalized.code === ErrorCode.NOT_FOUND;
}

/**
 * Type guard to check if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  const normalized = normalizeError(error);
  return normalized.retryable;
}
